/**
 * The drawn ground — a displaced, flat-shaded mesh replacing `flight/world.ts`'s
 * single quad wherever a region has relief (`packages/engines flight/terrain-relief.ts`).
 *
 * ## One mesh, one draw call, no texture
 *
 * Every triangle carries its own three vertices with one colour on all of them
 * (`vertexColors`, the same `MeshBasicMaterial` convention every other baked
 * surface in this app uses), and that colour is a `BARON.PAL` entry picked by
 * the face's slope through `world/terrain-palette.ts`'s walk. So the relief is
 * expressed as *shading steps in the game's own palette* — the "1990 artists
 * with a modern GPU" reading — rather than as a texture, and a level face still
 * draws the exact `#4d5928` the original was measured at.
 *
 * ## Depth
 *
 * Opaque, `depthWrite` and `depthTest` left alone, no `renderOrder`, no
 * `polygonOffset`. lessons.md records this twice (2026-08-19 survey grid,
 * 2026-08-22 bullet pool): under this renderer's reversed-Z buffer, opting a
 * layer out of the depth buffer can stop it drawing at all, and coplanar
 * separation here is geometric — the same `GROUND_DATUM_OFFSET_M` the flat
 * plane already uses, so placed terrain decals sit above the surface by exactly
 * the margin they always did.
 *
 * ## Resolution
 *
 * A flight sim's ground is looked at from 200 m and from 26 km in the same
 * frame, so the grid is **radially graded**: a regular lattice in `u ∈ [-1,1]`
 * mapped through `x = u·H·(a + (1−a)u²)`, which puts fine quads under the
 * aeroplane and coarse ones at the horizon without a second mesh, a clipmap or
 * any per-frame work. At the shipped constants that is ~104 m under the nose,
 * ~150 m at 5 km, ~460 m at the 26 km far plane and ~1 km at the rim.
 *
 * **What a shipping version should do instead.** One static mesh sized to the
 * whole region is 295 000 triangles and ~21 MB of vertex buffers, held for the
 * whole flight. That is one draw call and no per-frame cost, which is why the
 * prototype takes it, but it is a lot of memory to spend on ground the pilot
 * cannot see. The rebuild is cheap enough to do differently — sampling the
 * field for a 30 km mesh measures 20-35 ms — so a camera-following tile, or
 * u6-opus's wrapped-lattice trick, would cut this by an order of magnitude at
 * the price of a periodic hitch or a second mesh.
 *
 * **The mesh interpolates linearly between samples, and the physics query now
 * reads this mesh rather than the field it was built from.** It did not always:
 * `world/ground-elevation.ts` used to evaluate the analytic `ReliefField`
 * exactly, and this comment used to claim the two "differ by the sagitta of one
 * quad — under 0.3 m at these slopes and spacings, which is well inside the
 * undercarriage's own tolerances". **Both halves of that were wrong**, and it
 * was a user-visible fall-through bug (report, 2026-08-24: *"landing on an
 * aerodrome or road … we actually fall through the floor"*).
 *
 * Measured over 20 000 points of Verdun, `drapeAt` and `ReliefField.heightAt`
 * differ by **1.14 m mean and 10.74 m worst** — 38× the claim — because the
 * error is not sagitta. The lattice is radially *graded* (see Resolution above)
 * and reaches ~1 km cells at the rim, while `terrain-relief.ts`'s finest fbm
 * octave has a wavelength of 2300/5.1 ≈ **451 m**: out there the mesh does not
 * sample that octave at all, so it aliases rather than chords, and no per-quad
 * tolerance bounds it. Nor was it inside the undercarriage's tolerances —
 * `flight/ground-contact.ts` takes a *single* sample at the pose origin and
 * places the wheels at `heightM + gearHeightM`, so a metre of disagreement is a
 * metre of aeroplane buried in the road.
 *
 * The fix was not to tighten the lattice (which cannot converge on an octave it
 * does not sample) but to stop having two answers: `sampleAt`/`drapeAt` is the
 * one surface, the renderer drapes through it and the sim queries through it.
 * **Rule, the same one the 2026-08-23 entry reached from the other side:** when
 * a comment claims two surfaces are close, write the assertion that *measures*
 * the gap — `terrain-relief.test.ts` now pins it at 0.
 */

import * as THREE from "three/webgpu";
import {
  colourToRgb,
  shadeTerrainFaceIndex,
  shiftRampIndex,
  GROUND_RELIEF_RAMP,
  type TerrainRamp,
} from "../world/terrain-palette.ts";
import type { GroundLattice } from "./lattice-clip.ts";
import type { ReliefField } from "@red-opus/engine";

/**
 * The flat plane's own sub-datum offset (`flight/world.ts`), reused so that a
 * region's flat `TERR` polygons keep the exact clearance over the ground they
 * have today.
 */
export const GROUND_DATUM_OFFSET_M = -0.08;

/** Lattice steps per axis. 384 is 294 912 triangles in one draw call — the perf table in docs/engineering/enhanced-terrain.md has the alternatives. */
export const RELIEF_GRID_STEPS = 384;

/**
 * Half-extent of the drawn surface, metres.
 *
 * It has to beat *the region*, not the far plane. The mesh is centred on the
 * start aerodrome and the camera rides the aeroplane, so the visible ground
 * reaches `distance-from-origin + 26 km`; with Verdun 107 x 89 km that is
 * comfortably past 30 km on an ordinary sortie, and a mesh sized to the far
 * plane alone would show its own rim. 80 km covers any point of any shipped
 * region from any other.
 *
 * The cost of buying it that way is memory — see the module doc's note on
 * what a shipping version should do instead.
 */
export const RELIEF_HALF_EXTENT_M = 80000;

/** Share of the grading that stays linear; the rest is the cubic that coarsens outward. */
const GRADE_LINEAR_SHARE = 0.25;

/** `u ∈ [-1,1]` → metres, fine near the middle and coarse at the rim. */
function gradeAxis(u: number, halfExtentM: number): number {
  return u * halfExtentM * (GRADE_LINEAR_SHARE + (1 - GRADE_LINEAR_SHARE) * u * u);
}

export interface ReliefGroundOptions {
  readonly steps?: number;
  readonly halfExtentM?: number;
  readonly ramp?: TerrainRamp;
}

export interface ReliefGround {
  readonly mesh: THREE.Mesh;
  readonly triangleCount: number;
  /**
   * The height of the **drawn** surface at a world XZ — the mesh's own
   * piecewise-linear interpolation, not the analytic field.
   *
   * Everything laid on the ground must drape through this rather than through
   * `relief.heightAt`, and the reason is visible from 2 000 ft. The two are
   * different approximations of the same surface: this mesh's lattice coarsens
   * from ~104 m under the nose to ~1 km at the rim, while a landcover plate is
   * subdivided at a fixed edge. Where the plate's chord cuts a ridge the mesh
   * resolves, the bare ground **pokes through the field** — measured at up to
   * ~3 m with a 320 m plate edge, which reads as scattered green blobs on
   * ploughed brown. Sampling the drawn surface makes the plate lie on the
   * ground by construction, and the 8 cm datum offset is then enough.
   */
  readonly sampleAt: (x: number, z: number) => number;
  /**
   * What everything **placed on** the ground drapes through: `sampleAt` with the
   * mesh's own sub-datum offset added back, so a thing authored at the datum
   * lands `GROUND_DATUM_OFFSET_M` above the drawn surface — exactly the 8 cm of
   * clearance it has over the flat world's plane.
   *
   * The two are one constant apart and that constant is the whole of a user's
   * 2026-08-23 "a lot of z-fighting" report: draping through `sampleAt` puts a
   * plate, a road or a hangar *exactly on* the surface, at zero clearance, which
   * is the one separation a depth buffer cannot arbitrate.
   */
  readonly drapeAt: (x: number, z: number) => number;
  /**
   * The lattice the mesh is built on, for `terrain-drape.ts` to cut plates and
   * hairlines against. Exposed rather than inferred because "the cells the
   * surface is actually made of" is the only thing that makes a drape coplanar
   * with it (`three/lattice-clip.ts`).
   */
  readonly lattice: GroundLattice;
  /**
   * Walks every face `shift` steps along its own ramp — the hour's dimming
   * (`flight/time-of-day.ts`'s `groundRampShiftFor`: 0 at noon, −4 at night).
   *
   * A colour rewrite, not a rebuild: the shade *position* of each face is kept
   * from the build, so this is one pass over the colour attribute (~5 ms for
   * 295 000 triangles) rather than 154 ms of re-sampling the height field. It
   * has to be live because ENHANCED SKY can be switched mid-flight, and a
   * ground that stayed at noon under a night sky is exactly the mismatch this
   * exists to remove.
   */
  readonly setRampShift: (shift: number) => void;
}

/**
 * Builds the surface. `relief.heightAt` is called once per lattice vertex —
 * `(steps + 1)²` times, ~37 000 at the default — which is the whole of the
 * build cost and is why `terrain-relief.ts` buckets its river segments.
 */
export function createReliefGround(relief: ReliefField, options: ReliefGroundOptions = {}): ReliefGround {
  const steps = options.steps ?? RELIEF_GRID_STEPS;
  const halfExtentM = options.halfExtentM ?? RELIEF_HALF_EXTENT_M;
  const ramp = options.ramp ?? GROUND_RELIEF_RAMP;

  const side = steps + 1;
  const xs = new Float64Array(side);
  for (let i = 0; i < side; i++) xs[i] = gradeAxis((i / steps) * 2 - 1, halfExtentM);

  // Sample the field once per lattice point, then share those samples between
  // the four triangles that touch each one.
  const heights = new Float64Array(side * side);
  for (let iz = 0; iz < side; iz++) {
    for (let ix = 0; ix < side; ix++) {
      heights[iz * side + ix] = relief.heightAt(xs[ix]!, xs[iz]!) + GROUND_DATUM_OFFSET_M;
    }
  }

  const triangleCount = steps * steps * 2;
  const positions = new Float32Array(triangleCount * 9);
  const colors = new Float32Array(triangleCount * 9);
  // One ramp position per triangle, kept so the hour can move it later.
  const rampIndices = new Uint8Array(triangleCount);
  let cursor = 0;
  let face = 0;

  const emit = (
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
  ): void => {
    // Geometric normal, the same convention `model-geometry.ts` falls back to.
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    const index = shadeTerrainFaceIndex(ramp, uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx);
    rampIndices[face++] = index;
    const [r, g, b] = colourToRgb(ramp.colours[index]!);
    const corners = [ax, ay, az, bx, by, bz, cx, cy, cz];
    for (let k = 0; k < 9; k++) positions[cursor + k] = corners[k]!;
    for (let k = 0; k < 3; k++) {
      colors[cursor + k * 3] = r;
      colors[cursor + k * 3 + 1] = g;
      colors[cursor + k * 3 + 2] = b;
    }
    cursor += 9;
  };

  for (let iz = 0; iz < steps; iz++) {
    for (let ix = 0; ix < steps; ix++) {
      const x0 = xs[ix]!, x1 = xs[ix + 1]!;
      const z0 = xs[iz]!, z1 = xs[iz + 1]!;
      const h00 = heights[iz * side + ix]!;
      const h10 = heights[iz * side + ix + 1]!;
      const h01 = heights[(iz + 1) * side + ix]!;
      const h11 = heights[(iz + 1) * side + ix + 1]!;
      // Wound counter-clockwise seen from above, so a geometric normal points up.
      emit(x0, h00, z0, x0, h01, z1, x1, h10, z0);
      emit(x1, h10, z0, x0, h01, z1, x1, h11, z1);
    }
  }

  const mesh = buildMesh(positions, colors);
  const setRampShift = rampShifter(mesh, ramp, colors, rampIndices, triangleCount);
  const sampleAt = buildSampler(xs, heights, steps, side);
  const lattice: GroundLattice = {
    steps,
    axisAt: (i) => xs[Math.max(0, Math.min(steps, i))]!,
    cellIndexAt: (v) => cellIndex(xs, v, steps),
  };
  return {
    mesh,
    triangleCount,
    sampleAt,
    drapeAt: (x, z) => sampleAt(x, z) - GROUND_DATUM_OFFSET_M,
    lattice,
    setRampShift,
  };
}

/** The one draw call: a non-indexed, flat-shaded, vertex-coloured mesh. */
function buildMesh(positions: Float32Array, colors: Float32Array): THREE.Mesh {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();
  return new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ vertexColors: true }));
}

/** `ReliefGround.setRampShift` — the hour's walk, as a colour rewrite over the kept ramp positions. */
function rampShifter(
  mesh: THREE.Mesh,
  ramp: TerrainRamp,
  colors: Float32Array,
  rampIndices: Uint8Array,
  triangleCount: number,
): (shift: number) => void {
  const colourAttribute = mesh.geometry.getAttribute("color");
  let applied = 0;
  return (shift: number): void => {
    if (shift === applied) return;
    applied = shift;
    for (let t = 0; t < triangleCount; t++) {
      const [r, g, b] = colourToRgb(ramp.colours[shiftRampIndex(ramp, rampIndices[t]!, shift)]!);
      for (let corner = 0; corner < 3; corner++) {
        const at = t * 9 + corner * 3;
        colors[at] = r;
        colors[at + 1] = g;
        colors[at + 2] = b;
      }
    }
    colourAttribute.needsUpdate = true;
  };
}

/** Largest `i` with `xs[i] <= value`, by binary search — `xs` is monotone because `gradeAxis` is. */
function cellIndex(xs: Float64Array, value: number, steps: number): number {
  let lo = 0;
  let hi = steps;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (xs[mid]! <= value) lo = mid;
    else hi = mid - 1;
  }
  return Math.max(0, Math.min(steps - 1, lo));
}

/**
 * Interpolates the drawn surface, using **the same diagonal split** the mesh is
 * built with. Bilinear would be the obvious choice and is wrong here: a quad's
 * two triangles are planar and bilinear is not, and on a 460 m quad over the
 * finest noise octave the two disagree by a metre or more — which is exactly
 * the size of error this sampler exists to remove.
 */
function buildSampler(
  xs: Float64Array,
  heights: Float64Array,
  steps: number,
  side: number,
): (x: number, z: number) => number {
  return (x: number, z: number): number => {
    const ix = cellIndex(xs, x, steps);
    const iz = cellIndex(xs, z, steps);
    const x0 = xs[ix]!;
    const z0 = xs[iz]!;
    const dx = xs[ix + 1]! - x0;
    const dz = xs[iz + 1]! - z0;
    const u = dx > 0 ? Math.min(1, Math.max(0, (x - x0) / dx)) : 0;
    const v = dz > 0 ? Math.min(1, Math.max(0, (z - z0) / dz)) : 0;
    const h00 = heights[iz * side + ix]!;
    const h10 = heights[iz * side + ix + 1]!;
    const h01 = heights[(iz + 1) * side + ix]!;
    const h11 = heights[(iz + 1) * side + ix + 1]!;
    // The build emits (x0,z0)-(x0,z1)-(x1,z0) and (x1,z0)-(x0,z1)-(x1,z1), so
    // the shared diagonal runs from (x0,z1) to (x1,z0), i.e. u + v = 1.
    return u + v <= 1
      ? h00 + (h10 - h00) * u + (h01 - h00) * v
      : h11 + (h01 - h11) * (1 - u) + (h10 - h11) * (1 - v);
  };
}
